import { useCallback } from 'react';
import { Button, Stack, Text } from '@adc/parallax-component-library';
import { Dialog } from '@tamagui/dialog';

import { ModalContent } from '~/components/ModalContent';
import { ModalOverlay } from '~/components/ModalOverlay';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

type ArchiveUserModalProps = {
  testID: string;
  userFullName: string;
  onArchive: () => Promise<void>;
};

export const ArchiveUserModal = ({
  testID,
  userFullName,
  onArchive,
}: ArchiveUserModalProps) => {
  const { closeModal } = useModal();

  const onClose = useCallback(() => {
    closeModal('ArchiveUser');
  }, [closeModal]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );

  const handleUserArchive = useCallback(async () => {
    await onArchive();
    onClose();
  }, [onClose, onArchive]);

  return (
    <Dialog open onOpenChange={onOpenChange} modal>
      <Dialog.Portal testID={buildTestId({ testID, suffix: 'portal' })}>
        <ModalOverlay testID={buildTestId({ testID, suffix: 'overlay' })} />

        <ModalContent
          testID={buildTestId({
            testID,
            suffix: 'content',
          })}
          key="content"
          maxWidth={800}
          gap="$6"
        >
          <Text type="$h3.medium">Confirm Archive User</Text>
          <Text type="$bodyBase.medium">
            Are you sure you want to archive this {userFullName}?
            <br />
            Archived users will lose the ability to sign in.
            <br />
            Users can still be restored from the Archive folder.
          </Text>
          <Stack
            testID={buildTestId({ testID, suffix: 'actions' })}
            flexDirection="row"
            justifyContent="flex-end"
            gap="$4"
          >
            <Button
              label="Cancel"
              aria-label="Cancel User Archivation"
              variant="secondary"
              testID={buildTestId({
                testID,
                suffix: ['cancel', 'button'],
              })}
              onPress={onClose}
            />
            <Button
              label="Archive"
              aria-label="Archive User"
              variant="primary"
              testID={buildTestId({
                testID,
                suffix: ['archive', 'button'],
              })}
              onPress={() => void handleUserArchive()}
            />
          </Stack>
        </ModalContent>
      </Dialog.Portal>
    </Dialog>
  );
};
